/* Copyright (c) 2023 Xperi. All Rights Reserved. */

import { ApplicationStartup } from '@tivocorp/tivo-webapp-loggers';
import {
    Error403LicensePlateCodeEnum,
    Error500CodeEnum,
    Error504CodeEnum,
    LicensePlate,
    SamlToken,
} from 'client-code/device-binding-service';
import { AuthConfiguration, Error401CodeEnum, Error403CodeEnum } from 'client-code/iptv-login';
import { AnyAction } from 'redux';
import {
    EmptyResponseError,
    LpExpiredError,
    PartialResponseError,
    UnauthenticatedError,
    UnauthorisedError,
} from '../../core/error';
import { AppStartOperationsLogger, VuLogger } from '../../core/logging';
import { DeviceModel } from '../../core/device/DeviceModel';
import { BrandingUiInfoKeys } from '../../core/entities/BrandingUiInfoKeys';
import { AuthConfigError } from '../../core/error/AuthConfigError';
import { LicensePlateError } from '../../core/error/LicensePlateError';
import { SamlTokenError } from '../../core/error/SamlTokenError';
import { SignInError } from '../../core/error/SignInError';
import { AuthServicesClientImpl } from '../../core/impls/AuthServicesClientImpl';
import { DefaultServicesClientImpl } from '../../core/impls/DefaultServiceClientImpl';
import { SlsServiceGlobalEndpointsService } from '../../core/infrastructure/SlsServiceGlobalEndpointsService';
import { Locale } from '../../core/locale/Locale';
import { LocaleKeys } from '../../core/locale/LocaleKeys';
import { Log } from '../../core/logging/Log';
import { ScreenTaskControllerTypes } from '../../core/navigation/ScreenTaskControllerTypes';
import { setScreenTaskController, showSignInScreen } from '../screens/screens.reducers';
import {
    clearSignInErrorAction,
    setLicensePlateUrlAction,
    setLpCodeAndExpirationTimeAction,
    setSignInButtonHighlightAction,
    setSignInErrorAction,
} from './signin.actions';
import {
    CLEAR_SIGN_IN_ERROR,
    SET_NEW_CODE_BTN_HIGHLIGHT,
    SET_LICENSE_PLATE_URL,
    SET_LP_CODE_AND_EXPIRATION,
    SET_SIGN_IN_ERROR,
} from './signin.types';
import { TokenRefreshMechansim } from '../../core/device/TokenRefreshMechanism';
import { IptvLoginError } from '../../core/error/IptvLoginError';
import { showSpinner, hideSpinner } from '../spinner/spinner.reducers';
import { NetworkManager } from '../../core/network/NetworkManager';
import { showNoNetworkOverlay } from '../overlay/nonetworkoverlay/nonetworkoverlay.reducers';
import { OverlayTypes } from '../overlay/OverlayTypes';
import { IOverlayDialog } from '../overlaydialog/overlaydialogprops';
import { showOverlayDialog } from '../overlaydialog/overlaydialog.reducers';

const TAG = 'signin';
const GLOBAL_MOUSE_TRAP: string = 'HideSignInOverlay';

let onSignInSuccessCallback;

const initialState: {
    error: LpExpiredError | SignInError | undefined;
    hasError: boolean;
    licensePlateUrl: string;
    licensePlateCode: string;
    lpExpirationTime: number;
    isButtonHighlight: boolean;
} = {
    error: undefined,
    hasError: false,
    licensePlateUrl: '',
    licensePlateCode: '',
    lpExpirationTime: 0,
    isButtonHighlight: true,
};

const signinReducer = (state = initialState, { type, payload } = <AnyAction>{}) => {
    switch (type) {
        case CLEAR_SIGN_IN_ERROR:
            return {
                ...state,
                error: payload.error,
                hasError: payload.hasError,
            };

        case SET_LICENSE_PLATE_URL:
            return {
                ...state,
                licensePlateUrl: payload.licensePlateUrl,
            };

        case SET_LP_CODE_AND_EXPIRATION:
            return {
                ...state,
                licensePlateCode: payload.licensePlateCode,
                lpExpirationTime: payload.lpExpirationTime,
            };

        case SET_SIGN_IN_ERROR:
            return {
                ...state,
                error: payload.error,
                hasError: payload.hasError,
            };

        case SET_NEW_CODE_BTN_HIGHLIGHT:
            return {
                ...state,
                isButtonHighlight: payload.isButtonHighlight,
            };

        default:
            return state;
    }
};

const formatErrorDescription = (description: string) => {
    const defaultServices = new DefaultServicesClientImpl();
    let formattedDescription = description;
    formattedDescription = formattedDescription.replace(
        'PHONE_CUSTOMER_SUPPORT_NUMBER',
        defaultServices.getBrandingUiInfoForKey(BrandingUiInfoKeys.PHONE_CUSTOMER_SUPPORT_NUMBER),
    );
    formattedDescription = formattedDescription.replace(
        'CUSTOMER_SUPPORT_NAME',
        defaultServices.getBrandingUiInfoForKey(BrandingUiInfoKeys.CUSTOMER_SUPPORT_NAME),
    );
    return formattedDescription;
};

const formatIptvLoginErrorDescription = (code?: string): string => {
    let description = Locale.getString(LocaleKeys.SIGN_IN_ERROR_OVERLAY_DESCRIPTION);
    if (code === Error403CodeEnum.AuthsNotAuthorized) {
        description = Locale.getString(LocaleKeys.SIGN_IN_ACCOUNT_LOCKED_DESCRIPTION);
    } else if (code === Error403CodeEnum.AuthsTooManyDevices)
        description = Locale.getString(LocaleKeys.SIGN_IN_TOO_MANY_DEVICES_DESCRIPTION);
    description = formatErrorDescription(description);
    return description;
};

const goGrabAndStoreAccessToken =
    (authServices: AuthServicesClientImpl, samlToken: string) => async (dispatch: Function) => {
        AppStartOperationsLogger.logAccessTokenGet();
        let iptvLoginResponse;
        try {
            iptvLoginResponse = await authServices.getIptvLoginBySamlToken(samlToken);
        } catch (error: any) {
            const samlErrors = [
                Error401CodeEnum.AuthsInvalidSamlToken,
                Error401CodeEnum.AuthsExpiredSamlToken,
            ];
            if (samlErrors.includes(error?.code)) throw error;

            let iptvError = error;
            if (!(error instanceof EmptyResponseError || error instanceof PartialResponseError)) {
                const iptvLoginError: any = error?.response?.data;
                iptvError = new IptvLoginError(
                    iptvLoginError?.message,
                    iptvLoginError?.code,
                    formatIptvLoginErrorDescription(iptvLoginError?.code),
                );
            }
            ApplicationStartup.error(
                'Signin',
                'Failed while preforming iptv operations',
                iptvError,
            );
            AppStartOperationsLogger.logAccessTokenFailed(iptvError);
            VuLogger.logLoginFailureEvent(iptvError);
            dispatch(hideSpinner());
            dispatch(setSignInErrorAction(iptvError));
            if (iptvError.getType() === Error403CodeEnum.AuthsNotAuthorized) {
                throw iptvError;
            }
            iptvLoginResponse = await authServices.waitForIptvLogin(samlToken);
            AppStartOperationsLogger.logAccessTokenRestoration();
            dispatch(clearSignInErrorAction());
            dispatch(showSpinner(Locale.getString(LocaleKeys.SIGN_IN_SPINNER_MESSAGE)));
        }
        AppStartOperationsLogger.logAccessTokenSuccessful();
        DeviceModel.setAccessTokenWithExpiry(
            iptvLoginResponse.accessToken,
            new Date(iptvLoginResponse.tokenExpirationTime),
        );
        DeviceModel.getTiVoSettings().setBodyId(iptvLoginResponse.bodyId);
        TokenRefreshMechansim.getInstance().scheduleAccessTokenRefresh();
    };

const goGrabSamlTokenAndDoIptvLogin =
    (authServices: AuthServicesClientImpl) => async (dispatch: Function) => {
        AppStartOperationsLogger.logSamlTokenGet();
        let samlToken;
        try {
            samlToken = await authServices.getSamlToken();
        } catch (error: any) {
            let samlTokenError = error;
            if (!(error instanceof EmptyResponseError || error instanceof PartialResponseError)) {
                const samlError = error?.response?.data;
                samlTokenError = new SamlTokenError(
                    samlError?.message,
                    samlError?.code,
                    formatErrorDescription(
                        Locale.getString(LocaleKeys.SIGN_IN_ERROR_OVERLAY_DESCRIPTION),
                    ),
                );
            }
            dispatch(showSignInScreen());
            dispatch(setSignInErrorAction(samlTokenError));
            samlToken = await authServices.waitForSamlToken();
            AppStartOperationsLogger.logSamlTokenRestoration();
            dispatch(clearSignInErrorAction());
            dispatch(showSpinner(Locale.getString(LocaleKeys.SIGN_IN_SPINNER_MESSAGE)));
        } finally {
            AppStartOperationsLogger.logSamlTokenSuccessful();
            DeviceModel.setSamlTokenWithExpiry(
                samlToken.samlToken,
                new Date(samlToken.samlTokenExpirationTime),
            );
            await Promise.resolve(
                dispatch(goGrabAndStoreAccessToken(authServices, samlToken.samlToken)),
            );
        }
    };

const doIptvLoginOperation =
    (authServices: AuthServicesClientImpl, samlToken: string) => async (dispatch: Function) => {
        ApplicationStartup.info(TAG, 'Inside doIptvLoginOperation');
        try {
            await Promise.resolve(dispatch(goGrabAndStoreAccessToken(authServices, samlToken)));
            onSignInSuccessCallback();
        } catch (error: any) {
            Log.e(TAG, 'doIptvLogin', error);
            if (
                error instanceof IptvLoginError &&
                error.getType() === Error403CodeEnum.AuthsNotAuthorized
            ) {
                throw error;
            }
            const iptvError: any = error?.response?.data;
            ApplicationStartup.error(TAG, 'Failed at doIptvLoginOperation()', error);
            AppStartOperationsLogger.logAccessTokenFailed(error);
            const samlError = [
                Error401CodeEnum.AuthsInvalidSamlToken,
                Error401CodeEnum.AuthsExpiredSamlToken,
            ];
            if (samlError.includes(iptvError?.code)) {
                await Promise.resolve(dispatch(goGrabSamlTokenAndDoIptvLogin(authServices)));
                ApplicationStartup.info(TAG, 'iptvLogin successful');
                onSignInSuccessCallback();
            } else {
                throw error;
            }
        }
    };

export const checkAuthentication = (): boolean => {
    try {
        DeviceModel.getAccessTokenWithExpiry();
        TokenRefreshMechansim.getInstance().scheduleAccessTokenRefresh();
        return true;
    } catch (error) {
        if (error instanceof UnauthorisedError) {
            Log.e(TAG, 'checkAuthentication:: User is unauthorised for current session');
            DeviceModel.restartApplication();
        }
        if (error instanceof UnauthenticatedError)
            Log.e(TAG, 'User is unauthenticated for current session');
        return false;
    }
};

export const doLicensePlateOperations =
    () =>
    // eslint-disable-next-line implicit-arrow-linebreak
    async (dispatch: Function): Promise<LicensePlate> => {
        ApplicationStartup.info(TAG, 'Inside doLicensePlateOperations');
        let licensePlate;
        const authServices = new AuthServicesClientImpl({
            iptvUrl: SlsServiceGlobalEndpointsService.getServiceEndpointByServiceName(
                SlsServiceGlobalEndpointsService.IPTV_LOGIN_SERVICE,
            ),
            deviceBinding: SlsServiceGlobalEndpointsService.getServiceEndpointByServiceName(
                SlsServiceGlobalEndpointsService.DEVICE_BINDING_SERVICE,
            ),
        });

        try {
            ApplicationStartup.info(
                TAG,
                'doLicensePlateOperations::authServices.getNewLicensePlate()',
            );
            AppStartOperationsLogger.logLicensePlateGet();
            licensePlate = await authServices.getNewLicensePlate();
            AppStartOperationsLogger.logLicensePlateSuccessful();
        } catch (error: any) {
            let lpError = error;
            if (!(error instanceof EmptyResponseError || error instanceof PartialResponseError)) {
                const licensePlateError = error?.response?.data;
                lpError = new LicensePlateError(
                    licensePlateError?.message,
                    licensePlateError?.code,
                    formatErrorDescription(
                        Locale.getString(LocaleKeys.SIGN_IN_ERROR_OVERLAY_DESCRIPTION),
                    ),
                );
            }
            ApplicationStartup.error(
                TAG,
                `Failed at 'doLicensePlateOperations::authServices.getNewLicensePlate()'`,
                lpError,
            );
            AppStartOperationsLogger.logLicensePlateFailed(lpError);
            dispatch(showSignInScreen());
            dispatch(setSignInErrorAction(lpError));
            VuLogger.logLoginFailureEvent(lpError);
            ApplicationStartup.info(
                TAG,
                'doLicensePlateOperations::authServices.waitForNewLicensePlate()',
            );
            licensePlate = await authServices.waitForNewLicensePlate();
            AppStartOperationsLogger.logLicensePlateRestoration();
            dispatch(clearSignInErrorAction());
        } finally {
            const lpExpTime =
                new Date(licensePlate.expirationTime).getTime() - new Date().getTime();
            dispatch(setLpCodeAndExpirationTimeAction(licensePlate!.licensePlateCode, lpExpTime));
            dispatch(clearSignInErrorAction());
        }
        return licensePlate;
    };

export const initiateSignin = () => async (dispatch: Function) => {
    ApplicationStartup.info(TAG, 'Inside initiateSignin');
    // eslint-disable-next-line no-async-promise-executor
    return new Promise<void>(async (resolve) => {
        onSignInSuccessCallback = resolve;
        let authConfig!: AuthConfiguration;
        let samlToken: SamlToken;

        const authServices = new AuthServicesClientImpl({
            iptvUrl: SlsServiceGlobalEndpointsService.getServiceEndpointByServiceName(
                SlsServiceGlobalEndpointsService.IPTV_LOGIN_SERVICE,
            ),
            deviceBinding: SlsServiceGlobalEndpointsService.getServiceEndpointByServiceName(
                SlsServiceGlobalEndpointsService.DEVICE_BINDING_SERVICE,
            ),
        });

        try {
            ApplicationStartup.info(TAG, 'Get AuthConfiguration');
            AppStartOperationsLogger.logAuthenticationConfigurationGet();
            authConfig = await authServices.getAuthConfiguration();

            AppStartOperationsLogger.logAuthenticationConfigurationSuccessful(
                authConfig.authenticationType,
                authConfig.domain,
                authConfig.licensePlateUrl,
            );
        } catch (error: any) {
            let authConfError = error;
            if (!(error instanceof EmptyResponseError || error instanceof PartialResponseError)) {
                const authError = error?.response?.data;
                authConfError = new AuthConfigError(
                    authError?.message,
                    authError?.code,
                    formatErrorDescription(
                        Locale.getString(LocaleKeys.SIGN_IN_ERROR_OVERLAY_DESCRIPTION),
                    ),
                );
            }
            dispatch(showSignInScreen());
            dispatch(setSignInErrorAction(authConfError));
            ApplicationStartup.error(
                TAG,
                `Failed at 'initiateSignin::authServices.getAuthConfiguration()'`,
                authConfError,
            );
            AppStartOperationsLogger.logAuthenticationConfigurationFailed(authConfError);
            VuLogger.logLoginFailureEvent(authConfError);
            ApplicationStartup.info(TAG, 'Waiting for AuthConfiguration.');
            authConfig = await authServices.waitForAuthConfiguration();
            AppStartOperationsLogger.logAuthenticationConfigurationRestoration();
            dispatch(clearSignInErrorAction());
        } finally {
            DeviceModel.getTiVoSettings().setProvisioningDomain(authConfig.domain);
            dispatch(setLicensePlateUrlAction(authConfig.licensePlateUrl));
        }

        const licensePlate: LicensePlate = await Promise.resolve(
            dispatch(doLicensePlateOperations()),
        );

        try {
            ApplicationStartup.info(TAG, 'Get SamlToken');
            AppStartOperationsLogger.logSamlTokenGet();
            samlToken = await authServices.getSamlToken();
            AppStartOperationsLogger.logSamlTokenSuccessful();
            DeviceModel.setSamlTokenWithExpiry(
                samlToken.samlToken,
                new Date(samlToken.samlTokenExpirationTime),
            );
        } catch (error: any) {
            let samlTokenError = error;
            if (error instanceof EmptyResponseError || error instanceof PartialResponseError) {
                dispatch(showSignInScreen());
                dispatch(setSignInErrorAction(samlTokenError));
                samlToken = await authServices.waitForSamlToken();
                AppStartOperationsLogger.logSamlTokenRestoration();
                dispatch(clearSignInErrorAction());
                dispatch(showSpinner(Locale.getString(LocaleKeys.SIGN_IN_SPINNER_MESSAGE)));
            } else {
                const samlError = error?.response?.data;
                samlTokenError = new SamlTokenError(
                    samlError?.message,
                    samlError?.code,
                    formatErrorDescription(
                        Locale.getString(LocaleKeys.SIGN_IN_ERROR_OVERLAY_DESCRIPTION),
                    ),
                );
                ApplicationStartup.error(
                    TAG,
                    `Failed at 'initiateSignin::authServices.getSamlToken()'`,
                    samlTokenError,
                );
                AppStartOperationsLogger.logSamlTokenFailed(samlTokenError);
                // show overlay only if internal server error since it is first time call to check
                // signin. no lp code is passed in first call so no other errors should come.
                // TODO: remove hard coded error code once schema is updated
                if (
                    samlError?.code === Error504CodeEnum.DbsIntegrationTimeout ||
                    samlError?.code === Error500CodeEnum.DbsInternalError
                ) {
                    dispatch(showSignInScreen());
                    dispatch(setSignInErrorAction(samlTokenError));
                    samlToken = await authServices.waitForSamlToken();
                    AppStartOperationsLogger.logSamlTokenRestoration();
                    dispatch(clearSignInErrorAction());
                }
            }
            VuLogger.logLoginFailureEvent(samlTokenError);
            if (!samlToken!) {
                dispatch(showSignInScreen());
                try {
                    ApplicationStartup.info(TAG, 'Polling SamlToken');
                    AppStartOperationsLogger.logSamlTokenGet();
                    samlToken = await authServices.pollSamlToken(licensePlate);
                    AppStartOperationsLogger.logSamlTokenSuccessful();
                    DeviceModel.setSamlTokenWithExpiry(
                        samlToken.samlToken,
                        new Date(samlToken.samlTokenExpirationTime),
                    );
                } catch (errorAfterPolling: any) {
                    let pollingError = errorAfterPolling;
                    if (errorAfterPolling instanceof LpExpiredError) {
                        dispatch(setSignInErrorAction(errorAfterPolling));
                        ApplicationStartup.error(
                            TAG,
                            'saml token fetching expired',
                            errorAfterPolling,
                        );
                        Log.e(TAG, 'saml token fetching expired');
                    } else if (
                        errorAfterPolling instanceof EmptyResponseError ||
                        errorAfterPolling instanceof PartialResponseError
                    ) {
                        dispatch(showSignInScreen);
                        dispatch(setSignInErrorAction(pollingError));
                        ApplicationStartup.error(
                            TAG,
                            'saml token partial or empty response',
                            error,
                        );
                        Log.e(TAG, 'saml token partial or empty response');
                        samlToken = await authServices.waitForSamlToken();
                        AppStartOperationsLogger.logSamlTokenRestoration();
                        dispatch(clearSignInErrorAction());
                    } else {
                        const samlPollingError = errorAfterPolling?.response?.data;
                        pollingError = new SamlTokenError(
                            samlPollingError?.message,
                            samlPollingError?.code,
                            formatErrorDescription(
                                Locale.getString(LocaleKeys.SIGN_IN_ERROR_OVERLAY_DESCRIPTION),
                            ),
                        );
                        if (
                            samlPollingError.code !== Error403LicensePlateCodeEnum.DbsNotAuthorized
                        ) {
                            AppStartOperationsLogger.logSamlTokenFailed(pollingError);
                            dispatch(setSignInErrorAction(pollingError));
                        }
                        Log.e(
                            TAG,
                            'pollSamlToken: some unidentified error, no more saml polling',
                            errorAfterPolling,
                        );
                        AppStartOperationsLogger.logSamlTokenFailed(pollingError);
                    }
                }
            }
        }

        if (samlToken!) {
            dispatch(showSpinner(Locale.getString(LocaleKeys.SIGN_IN_SPINNER_MESSAGE)));
            dispatch(showSignInScreen());
        }

        try {
            if (samlToken!) {
                await Promise.resolve(
                    dispatch(doIptvLoginOperation(authServices, samlToken!.samlToken)),
                );
            }
        } catch (error: any) {
            Log.e(TAG, 'initiateSignin::doIptvLoginOperation()', error);
            let iptvError = error;
            if (
                error instanceof IptvLoginError &&
                error.getType() === Error403CodeEnum.AuthsNotAuthorized
            ) {
                // when not authorized, we don't want to continue with service calls.
                // also we don't want to do anything else, just show user the error.
                // and a overlay to exit the app.
            } else {
                if (
                    !(error instanceof EmptyResponseError || error instanceof PartialResponseError)
                ) {
                    const iptvLoginError = error?.response?.data;
                    iptvError = new IptvLoginError(
                        iptvLoginError?.message,
                        iptvLoginError?.code,
                        formatIptvLoginErrorDescription(iptvLoginError?.code),
                    );
                }
                ApplicationStartup.error(
                    TAG,
                    `Failed at 'initiateSignin::doIptvLoginOperation()'`,
                    iptvError,
                );
                AppStartOperationsLogger.logAccessTokenFailed(iptvError);
                dispatch(setSignInErrorAction(iptvError));
                await authServices.waitForIptvLogin(samlToken!.samlToken);
                AppStartOperationsLogger.logAccessTokenRestoration();
                dispatch(hideSpinner());
            }
        }
    });
};

export const onLpCodeExpiration = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    const { isOverlayDialogActive } = getState().overlayDialog;
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.LP_CODE_EXPIRED_SIGNIN_TASK));
    if (isOverlayDialogActive) dispatch(setScreenTaskController(screenTaskController));
};

/**
 * Below reducers are related to operations on key events.
 */

export const onKeyOkSignInDefaultOperations = () => (dispatch: Function) => {
    if (NetworkManager.isNetworkDisconnected()) {
        dispatch(showNoNetworkOverlay());
    }
};

export const onKeyOkSignInLpOperations = () => (dispatch: Function) => {
    if (NetworkManager.isNetworkDisconnected()) {
        dispatch(showNoNetworkOverlay());
    } else {
        Promise.resolve(dispatch(onKeyOkSignInOperations()));
    }
};

export const onKeyOkSignInOperations = () => async (dispatch: Function) => {
    ApplicationStartup.info(TAG, 'Inside onKeyOkSignInOperations');
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.DEFAULT_SIGN_IN_TASKS));

    const authServices = new AuthServicesClientImpl({
        iptvUrl: SlsServiceGlobalEndpointsService.getServiceEndpointByServiceName(
            SlsServiceGlobalEndpointsService.IPTV_LOGIN_SERVICE,
        ),
        deviceBinding: SlsServiceGlobalEndpointsService.getServiceEndpointByServiceName(
            SlsServiceGlobalEndpointsService.DEVICE_BINDING_SERVICE,
        ),
    });

    const licensePlate = await Promise.resolve(dispatch(doLicensePlateOperations()));
    dispatch(setSignInButtonHighlightAction(false));

    let samlToken: SamlToken;
    try {
        ApplicationStartup.info(TAG, 'Polling SamlToken inside onKeyOkSignInOperations');
        AppStartOperationsLogger.logSamlTokenGet();
        samlToken = await authServices.pollSamlToken(licensePlate);

        AppStartOperationsLogger.logSamlTokenSuccessful();
        DeviceModel.setSamlTokenWithExpiry(
            samlToken.samlToken,
            new Date(samlToken.samlTokenExpirationTime),
        );
    } catch (error: any) {
        const samlOkKeyError = error;
        if (error instanceof LpExpiredError) {
            dispatch(setSignInErrorAction(error));
            ApplicationStartup.error(
                TAG,
                'onKeyOkSignInOperations: saml token fetching expired',
                error,
            );
            Log.e(TAG, 'onKeyOkSignInOperations: saml token fetching expired');
        } else if (error instanceof PartialResponseError || error instanceof EmptyResponseError) {
            dispatch(setSignInErrorAction(samlOkKeyError));
            ApplicationStartup.error(
                TAG,
                'onKeyOkSignInOperations: saml token partial or empty response',
                error,
            );
            Log.e(TAG, 'onKeyOkSignInOperations: saml token partial or empty response');
            samlToken = await authServices.waitForSamlToken();
            AppStartOperationsLogger.logSamlTokenRestoration();
            dispatch(clearSignInErrorAction());
        } else {
            AppStartOperationsLogger.logSamlTokenFailed(error);

            ApplicationStartup.error(
                TAG,
                `Failed at 'onKeyOkSignInOperations::authServices.pollSamlToken(licensePlate)`,
                error,
            );
            Log.e(TAG, '', error);
        }
    }

    try {
        if (samlToken!) {
            dispatch(showSpinner(Locale.getString(LocaleKeys.SIGN_IN_SPINNER_MESSAGE)));
            await Promise.resolve(
                dispatch(doIptvLoginOperation(authServices, samlToken!.samlToken)),
            );
        }
    } catch (error: any) {
        Log.e(TAG, 'onKeyOkSignInOperations::doIptvLoginOperation()', error);
        let iptvLoginError = error;
        if (!(error instanceof EmptyResponseError || error instanceof PartialResponseError)) {
            const iptvError: any = error?.response?.data;
            iptvLoginError = new IptvLoginError(
                iptvError?.message,
                iptvError?.code,
                formatIptvLoginErrorDescription(iptvError?.code),
            );
            ApplicationStartup.error(
                TAG,
                'Failed at onKeyOkSignInOperations::doIptvLoginOperation()',
                iptvLoginError,
            );
        }
    }
};

export const onKeyOkSignInOverlayOperations = () => () => {
    VuLogger.logAppExitEvent(VuLogger.AppExitActions.EXIT);
    DeviceModel.closeApplication();
};

export const onMouseClickSignInOverlayOperations =
    () => (dispatch: Function, getState: Function) => {
        const { value } = getState().inputFactory;

        if (value !== GLOBAL_MOUSE_TRAP) {
            dispatch(onKeyOkSignInOverlayOperations());
        }
    };

export const showSignInErrorOverlay =
    (error: SignInError) => (dispatch: Function, getState: Function) => {
        const { uiProperties } = getState().overlayDialog;
        if (uiProperties.overlayType === OverlayTypes.NO_NETWORK) return;
        const overlayDialogProps: IOverlayDialog = {
            description: error?.getDescription(),
            hasDimmer: true,
            hasSeperator: false,
            isPersistent: true,
            messageCode: error?.getCode(),
            mouseTrapValue: GLOBAL_MOUSE_TRAP,
            options: [
                {
                    text: Locale.getString(LocaleKeys.SOS_OVERLAY_OPTION_EXIT),
                    hasSelectMark: false,
                    isSelected: true,
                },
            ],
            overlayType: OverlayTypes.SIGN_IN_ERROR,
            title: error.getTitle(),
        };
        dispatch(showOverlayDialog(overlayDialogProps));
        dispatch(setScreenTaskController(ScreenTaskControllerTypes.SIGN_IN_ERROR_OVERLAY_TASKS));
    };

export default signinReducer;
